import {CommonActions} from '@react-navigation/native';
import React from 'react';
import {StyleSheet, View} from 'react-native';

import {BackButton} from '../../components/BackButton';
import {MoodLightList} from '../../components/MoodLightList';
import {withConnectedGuard} from '../../components/hocs/withConnectedGuard';
import {withDeeplink} from '../../components/hocs/withDeeplink';
import {withUserGuard} from '../../components/hocs/withUserGuard';
import {Screens} from '../../constants/navigation';
import {UnitLighting} from '../../contexts/unit-lighting';
import {useAdaptiveSafeArea} from '../../lib/hooks/useAdaptiveSafeArea';
import {useBackPress} from '../../lib/hooks/useBackPress';
import {HeatProfileEditNavigator} from '../../navigators/RootStackNavigator';
import {colors, fillStyle} from '../../styles';

export interface Props {
  selectedId?: string;
}

type ScreenProps = HeatProfileEditNavigator<typeof Screens.SelectMoodLight>;

const Screen: React.FC<ScreenProps> = ({route, navigation}) => {
  const insets = useAdaptiveSafeArea();
  const {selectedId} = route.params ?? {};
  const unitLighting = UnitLighting.useContainer();

  const goBack = React.useCallback((selectedMoodLightId?: string) => {
    navigation.dispatch(state => {
      // Navigate to previous screen while also setting its params by merging the previous with new ones and pop current screen of the stack
      const routes = state.routes
        .slice(0, -1)
        .map(({name, params}, index, array) =>
          index === array.length - 1
            ? {
                name,
                params: {...params, selectedMoodLightId},
              }
            : {name, params},
        );

      return CommonActions.reset({
        index: routes.length - 1,
        routes,
      });
    });
  }, []);

  const onBack = React.useCallback(() => {
    unitLighting.setPreviewLight();

    goBack(selectedId);
  }, [unitLighting.setPreviewLight, goBack, selectedId]);

  React.useEffect(() => {
    navigation.setOptions({
      headerLeft: ({disabled}) => (
        <BackButton onPress={onBack} {...{disabled}} />
      ),
    });
  }, [onBack]);

  const onBackPress = React.useCallback(() => {
    onBack();

    return true;
  }, [onBack, navigation]);

  useBackPress(onBackPress);

  return (
    <View
      style={[
        styles.container,
        {
          paddingTop: insets.top + 60,
          paddingBottom: insets.bottom,
          backgroundColor: colors.black,
        },
      ]}>
      <MoodLightList
        onItemToggle={moodLight => {
          navigation.setParams({selectedId: moodLight?.id ?? undefined});

          unitLighting.setPreviewLight(moodLight);
        }}
        onItemPress={value => {
          goBack(value?.id);
        }}
        {...{selectedId}}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {...fillStyle, flexDirection: 'column', justifyContent: 'center'},
});

export const SelectMoodLightScreen = withDeeplink(
  withUserGuard(withConnectedGuard(Screen)),
);
